import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Matrice permessi dinamici caricata dal backend
  const [permissions, setPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);

  // Check session via /auth/me endpoint (reads httpOnly cookie on server)
  const loadUser = useCallback(async () => {
    try {
      // The cookie is sent automatically with credentials: 'include'
      const response = await authApi.getMe();
      if (response.data?.user) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (err) {
      // Not authenticated or token expired
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Carica TUTTI i permessi dell'utente in una singola chiamata
  const loadPermissions = useCallback(async () => {
    if (!user) {
      setPermissions({});
      setIsSuperuser(false);
      setPermissionsLoaded(false);
      return;
    }
    
    setPermissionsLoading(true);
    try {
      // Nuova chiamata unica che ritorna tutti i permessi
      const response = await fetch('/api/permissions/my', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || {});
        setIsSuperuser(data.is_superuser || false);
      } else {
        console.warn('Failed to load permissions, using defaults');
        setPermissions({});
        setIsSuperuser(user?.role === 'admin');
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setPermissions({});
      setIsSuperuser(user?.role === 'admin');
    } finally {
      setPermissionsLoading(false);
      setPermissionsLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Carica permessi quando l'utente cambia
  useEffect(() => {
    if (user) {
      loadPermissions();
    } else {
      setPermissions({});
      setIsSuperuser(false);
      setPermissionsLoaded(false);
    }
  }, [user, loadPermissions]);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authApi.login(email, password);
      const { user: userData } = response.data;
      
      // Cookie is set automatically by the server response
      // We just need to update the local state
      setUser(userData);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Errore durante il login';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear the httpOnly cookie
      await authApi.logout();
    } catch (err) {
      // Ignore errors, clear state anyway
      console.error('Logout error:', err);
    }
    setUser(null);
    setPermissions({});
    setIsSuperuser(false);
    setPermissionsLoaded(false);
  };

  const refreshUser = async () => {
    await loadUser();
  };

  // Funzione helper per verificare un permesso dalla matrice dinamica
  // Ritorna true se l'utente ha il permesso feature_key abilitato
  const hasPermission = useCallback((featureKey) => {
    // Admin è sempre autorizzato (superuser)
    if (isSuperuser || user?.role === 'admin') return true;
    
    // Verifica nella matrice permessi caricata
    return permissions[featureKey] === true;
  }, [user, permissions, isSuperuser]);

  // Helper per verificare se l'utente può accedere a un lead specifico
  // Combina permesso funzionale + ownership
  const canAccessLead = useCallback((lead) => {
    if (!user || !lead) return false;
    
    // Admin, PM, Administration possono accedere a tutti i lead
    if (['admin', 'project_manager', 'administration'].includes(user.role)) {
      return true;
    }
    
    // Tecnico può accedere SOLO se il lead è assegnato a lui
    if (user.role === 'external_technician') {
      return lead.assigned_technician_id === user.id;
    }
    
    return false;
  }, [user]);

  // Helper per verificare se l'utente può creare appuntamenti per un lead
  // Combina: permesso funzionale 'create_appointments' + ownership
  const canCreateAppointmentForLead = useCallback((lead) => {
    if (!user) return false;
    
    // Step 1: Verifica permesso funzionale
    if (!hasPermission('create_appointments')) {
      return false;
    }
    
    // Step 2: Verifica ownership/scope
    if (lead) {
      return canAccessLead(lead);
    }
    
    // Senza lead (appuntamento personale), permesso base è sufficiente
    return true;
  }, [user, hasPermission, canAccessLead]);

  const isAdmin = user?.role === 'admin';
  const isAdministration = user?.role === 'administration';
  const isProjectManager = user?.role === 'project_manager';
  const isTechnician = user?.role === 'external_technician';
  const isFurnitureSales = user?.role === 'furniture_sales';

  const hasRole = (roles) => {
    if (!user) return false;
    if (!Array.isArray(roles)) roles = [roles];
    return roles.includes(user.role);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refreshUser,
    isAdmin,
    isAdministration,
    isProjectManager,
    isTechnician,
    isFurnitureSales,
    hasRole,
    isAuthenticated: !!user,
    // Nuove funzioni per permessi dinamici
    permissions,
    permissionsLoading,
    permissionsLoaded,
    hasPermission,
    canAccessLead,
    canCreateAppointmentForLead,
    loadPermissions,
    isSuperuser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
